/** @jsx jsx */

import {
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { EntityType } from "@/core/types";
import { formatFullName } from "@/data/persons";
import { RelatedEntityKey, useRelatedWorkersQuery } from "@/data/related";
import { router } from "@/views/router";

interface RelatedWorkersListProps {
  entityType: EntityType;
  entityId: string | number;
  url: string;
  entityKey: RelatedEntityKey;
}

export const RelatedWorkersList: FC<RelatedWorkersListProps> = ({ entityType, entityId, entityKey, url }) => {
  const { t } = useTranslation("session");

  const { data } = useRelatedWorkersQuery({
    entityType,
    entityId,
    url,
  });

  const columns = {
    worker: {
      sx: {
        flexGrow: 1,
        alignSelf: "flex-start",
      },
    },
    email: {
      sx: {
        width: [null, null, null, "50%"],
        justifyContent: ["flex-start", null, "flex-end"],
      },
    },
  };

  return (
    <Flex gap="xs" flexDirection="column">
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={columns.worker}>
            {t(`RELATED_ENTITIES.${entityKey.toUpperCase()}.HEADER`)}
          </ListHeaderCell>
          <ListHeaderCell columnConfig={columns.email}>
            {t(`RELATED_ENTITIES.${entityKey.toUpperCase()}.EMAIL`)}
          </ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {data.content.map(worker => (
          <ListItem key={worker.id}>
            <ListCellsWrapper
              as={Link}
              to={{ pathname: router.get(`consultant-profiles.detail`, { consultantProfileId: worker.id }).path }}>
              <ListItemCell columnConfig={columns.worker}>
                <Flex gap="xs">
                  <LazyUserAvatar user={worker} />
                  <Flex flexDirection="column" justifyContent="center">
                    <Paragraph bold ellipsis>
                      {formatFullName(worker)}
                    </Paragraph>
                    {worker.position && <Paragraph color="n700">{worker.position.name}</Paragraph>}
                  </Flex>
                </Flex>
              </ListItemCell>

              <ListItemCell columnConfig={columns.email}>
                <Paragraph>{worker.email}</Paragraph>
              </ListItemCell>
            </ListCellsWrapper>
          </ListItem>
        ))}
      </List>
    </Flex>
  );
};
